/**
 * Painel de insights de uma planilha.
 *
 * Somente leitura por construção: faz um GET da planilha, calcula tudo em
 * memória e não expõe nenhuma ação de escrita. Não adquire lock de edição, então
 * abrir esta tela nunca bloqueia quem está editando a planilha.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiPrinter } from "react-icons/fi";
import { LoadingSpinner } from "@components/common";
import { planilhaService } from "@services/planilhaService";
import { Planilha, ItemPlanilha } from "@models/index";
import { calcularInsights } from "@utils/planilha-insights";
import {
  Cartao,
  Indicador,
  Medidor,
  NumeroHeroi,
  SemDados,
  VizEstilos,
  horas,
  moeda,
  percentual,
} from "@components/insights/viz";
import { ParetoItens } from "@components/insights/ParetoItens";
import { ConcentracaoFornecedores } from "@components/insights/ConcentracaoFornecedores";
import { DesempenhoFornecedores } from "@components/insights/DesempenhoFornecedores";

export const PlanilhaInsights: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [planilha, setPlanilha] = useState<Planilha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelado = false;

    (async () => {
      try {
        const dados = await planilhaService.getById(id);
        if (!cancelado) setPlanilha(dados);
      } catch (e: any) {
        if (!cancelado) setErro(e?.message || "Não foi possível carregar a planilha.");
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [id]);

  const insights = useMemo(() => {
    if (!planilha) return null;
    const itens = ((planilha as any).itens ?? []) as ItemPlanilha[];
    return calcularInsights(planilha, itens);
  }, [planilha]);

  useEffect(() => {
    if (insights) document.title = `Insights · ${insights.evento}`;
  }, [insights]);

  if (carregando) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (erro || !insights) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="alert alert-error">
          <span>{erro || "Planilha não encontrada."}</span>
        </div>
        <Link to="/planilhas" className="btn btn-ghost btn-sm mt-4">
          <FiArrowLeft className="h-4 w-4" /> Voltar para planilhas
        </Link>
      </div>
    );
  }

  const cobertura = insights.coberturaTresPropostas;
  const tomCobertura = cobertura >= 0.9 ? "bom" : cobertura >= 0.6 ? "atencao" : "critico";

  return (
    <div className="mx-auto max-w-7xl space-y-5 pb-12">
      <VizEstilos />

      {/* Cabeçalho */}
      <header className="flex flex-wrap items-start justify-between gap-4 print:hidden">
        <div className="min-w-0">
          <Link
            to={`/planilhas/editar/${id}`}
            className="mb-1 inline-flex items-center gap-1 text-sm text-base-content/60 hover:text-primary"
          >
            <FiArrowLeft className="h-3.5 w-3.5" /> Voltar para a planilha
          </Link>
          <h1 className="truncate text-2xl font-bold text-base-content">
            {insights.evento}
          </h1>
          <p className="text-sm text-base-content/70">
            {[insights.cliente, insights.local, insights.periodo]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>
          <FiPrinter className="h-4 w-4" /> Imprimir
        </button>
      </header>

      {/* Número que a tela lidera + indicadores de apoio */}
      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="viz rounded-xl border border-base-300 bg-base-100 p-5">
          <NumeroHeroi
            rotulo="Economia por negociação"
            valor={moeda(insights.economiaTotal)}
            apoio={
              insights.economiaTotal > 0 ? (
                <>
                  {percentual(insights.economiaPercentual, 1)} abaixo do cenário em que
                  cada item fosse fechado pela proposta mais cara recebida
                  ({moeda(insights.custoPiorCenario)}).
                </>
              ) : (
                <>
                  Ainda não há itens com mais de uma proposta para comparar. A economia
                  aparece quando houver concorrência.
                </>
              )
            }
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Indicador
            rotulo="Custo total"
            valor={moeda(insights.custoTotal)}
            apoio={`${insights.itens.length} linhas com valor`}
          />
          <Indicador
            rotulo="Itens cotados"
            valor={String(insights.itensCotaveis)}
            apoio={`${insights.totalCotacoes} cotações solicitadas`}
          />
          <Indicador
            rotulo="Propostas recebidas"
            valor={String(insights.totalRespostas)}
            apoio={
              insights.totalRecusas > 0
                ? `${insights.totalRecusas} ${insights.totalRecusas === 1 ? "recusa" : "recusas"}`
                : "nenhuma recusa"
            }
          />
          <Indicador
            rotulo="Tempo médio de resposta"
            valor={
              insights.horasMediaResposta !== undefined
                ? horas(insights.horasMediaResposta)
                : "—"
            }
            apoio="da solicitação até a proposta"
          />
        </div>
      </section>

      {insights.divergenciaDeTotal && (
        <p className="flex items-start gap-2 rounded-lg bg-warning/10 px-3 py-2 text-sm">
          <span aria-hidden>⚠️</span>
          <span className="text-base-content/80">
            O total somado das linhas ({moeda(insights.custoTotal)}) não coincide com o
            total gravado na planilha ({moeda(insights.custoGravado)}). Este painel usa a
            soma das linhas, que é a fonte confiável.
          </span>
        </p>
      )}

      <ParetoItens insights={insights} />

      <ConcentracaoFornecedores insights={insights} />

      {/* Riscos e pendências */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Cartao
          titulo="Cobertura de cotação"
          descricao="Proporção de itens com três propostas válidas, o esperado pelo FACITE."
        >
          <Medidor
            fracao={cobertura}
            rotulo={`${insights.itensComTresPropostas} de ${insights.itensCotaveis} itens com 3 propostas`}
            tom={tomCobertura}
            detalhe={
              insights.itensSemProposta > 0
                ? `${insights.itensSemProposta} ${insights.itensSemProposta === 1 ? "item ainda não recebeu nenhuma proposta" : "itens ainda não receberam nenhuma proposta"}.`
                : "Todos os itens cotados já receberam ao menos uma proposta."
            }
          />

          {insights.fornecedoresQueNaoAbriram.length > 0 && (
            <div className="mt-5 border-t border-base-300 pt-4">
              <p className="text-sm font-medium text-base-content">
                Nunca abriram o link recebido
              </p>
              <ul className="mt-2 space-y-1 text-sm text-base-content/70">
                {insights.fornecedoresQueNaoAbriram.slice(0, 5).map((n) => (
                  <li key={n} className="truncate" title={n}>
                    · {n}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-base-content/60">
                Um lembrete pela planilha costuma resolver.
              </p>
            </div>
          )}
        </Cartao>

        <Cartao
          titulo="Propostas fora da curva"
          descricao="Itens em que a maior proposta supera a menor em 1,8× ou mais."
        >
          {insights.spreadsAtipicos.length === 0 ? (
            <SemDados>
              Nenhuma diferença atípica entre propostas. As cotações estão coerentes
              entre si.
            </SemDados>
          ) : (
            <>
              <ul className="space-y-3">
                {insights.spreadsAtipicos.slice(0, 5).map((i) => (
                  <li key={i.id} className="text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="min-w-0 truncate font-medium" title={i.descricao}>
                        {i.numero} — {i.descricao}
                      </span>
                      <span className="shrink-0 tabular-nums font-semibold text-base-content">
                        {i.spread.toLocaleString("pt-BR", {
                          maximumFractionDigits: 1,
                        })}
                        ×
                      </span>
                    </div>
                    <p className="text-xs text-base-content/60 tabular-nums">
                      menor {moeda(i.vencedora?.valorUnitario ?? 0)} · maior{" "}
                      {moeda(i.maisCara?.valorUnitario ?? 0)} (unitário)
                    </p>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-base-content/60">
                Diferença grande costuma indicar descritivo ambíguo — os fornecedores
                cotaram coisas diferentes — antes de indicar preço bom.
              </p>
            </>
          )}
        </Cartao>
      </div>

      <DesempenhoFornecedores insights={insights} />

      <p className="text-center text-xs text-base-content/50">
        Painel somente leitura. Todos os números são calculados a partir das linhas da
        planilha no momento da abertura.
      </p>
    </div>
  );
};

export default PlanilhaInsights;
